/**
 * Theme handling for the **new admin tree only**.
 *
 * The resolved theme is written onto the new tree's own root element, never
 * onto `<html>`: the old admin must be unable to notice this file exists. Every
 * colour is a CSS custom property keyed off that element (see
 * `next/styles/_tokens.scss`).
 *
 * Three states are supported:
 *   - 'light'  : forced light
 *   - 'dark'   : forced dark
 *   - 'system' : follow the OS preference (default, no attribute is written)
 *
 * There is no inline script in `index.html` — touching that file would mean
 * touching the old admin. The new tree paints its own root once React mounts,
 * which costs a frame and buys total isolation.
 */

export const THEME_STORAGE_KEY = 'ws-admin-next-theme';

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const prefersDark = () => (
  isBrowser
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-color-scheme: dark)').matches
);

/** Reads the stored preference, falling back to 'system'. */
export const getStoredTheme = () => {
  if (!isBrowser) {
    return THEMES.SYSTEM;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === THEMES.LIGHT || stored === THEMES.DARK ? stored : THEMES.SYSTEM;
  } catch (error) {
    // Private mode / blocked storage: fall back to the system preference.
    return THEMES.SYSTEM;
  }
};

/** The theme actually rendered right now ('light' or 'dark'). */
export const getResolvedTheme = (preference = getStoredTheme()) => {
  if (preference === THEMES.LIGHT || preference === THEMES.DARK) {
    return preference;
  }

  return prefersDark() ? THEMES.DARK : THEMES.LIGHT;
};

/**
 * Persists the preference. Nothing is written to the DOM here on purpose: the
 * attribute belongs on the new tree's own root, which React renders from state
 * (see `NextRoot`). Writing to `document.documentElement` — as the old draft of
 * this file did — would put the attribute on `<html>`, where the old admin's
 * stylesheets could start reacting to it.
 */
export const setTheme = preference => {
  if (!isBrowser) {
    return;
  }

  try {
    if (preference === THEMES.SYSTEM) {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch (error) {
    // Private mode / blocked storage: the choice still holds for this session.
  }
};

/**
 * Cycles light -> dark -> system -> light and returns the new preference.
 */
export const cycleTheme = (current = getStoredTheme()) => {
  const next = {
    [THEMES.LIGHT]: THEMES.DARK,
    [THEMES.DARK]: THEMES.SYSTEM,
    [THEMES.SYSTEM]: THEMES.LIGHT
  }[current] || THEMES.LIGHT;

  setTheme(next);
  return next;
};

/**
 * Notifies the caller when the OS preference changes, so a 'system' theme
 * re-renders its icon. Returns an unsubscribe function.
 */
export const subscribeToSystemTheme = callback => {
  if (!isBrowser || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = event => callback(event.matches ? THEMES.DARK : THEMES.LIGHT);

  // Safari < 14 only supports the deprecated listener API.
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }

  query.addListener(handler);
  return () => query.removeListener(handler);
};
